// React
import React, { useEffect, useMemo, useState } from "react";

// MUI
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";

// Your themed button
import Button from "components/CustomButtons/Button.js";
import { createProjectAPI } from "views/APIs/ProjectsUtility";
import {
  uploadProjectFileValidation,
  deleteUploadedFileValidation,
} from "views/APIs/ToolboxUtility";

export default function ProjectDetailsScreen({
  name,
  setName,
  description,
  setDescription,
  projectsList = [],
  onNext,
  associatedProjectId,
  setAssociatedProjectId,
  onUpdate,
  projectId,
  fileList = [],
  setFileList,
}) {
  const images = require.context("assets/img/create_cards_16_9/", true);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const CREATE_SENTINEL = "__create_new_project__";
  const [associatedProjects, setAssociatedProjects] = useState([]);
  const inputId = useMemo(
    () => `project-attachment-${Math.random().toString(36).slice(2)}`,
    []
  );

  useEffect(() => {
    setAssociatedProjects([
      ...projectsList,
      { _id: CREATE_SENTINEL, name: "+ Create new project" },
    ]);
  }, [projectsList]);

  const whiteInput = { backgroundColor: "#fff" };

  const handleAssociatedProjectChange = (e) => {
    const val = e.target.value;
    if (val === CREATE_SENTINEL) {
      setCreateOpen(true);
      return;
    }
    setAssociatedProjectId(val);
  };

  const handleClose = () => {
    setCreateOpen(false);
    setNewName("");
    setNewDesc("");
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const created = await createNewProject(newName.trim(), newDesc.trim());
      setAssociatedProjects((prev) => [
        ...prev.filter((p) => p._id !== CREATE_SENTINEL),
        { _id: created._id, name: created.name },
        { _id: CREATE_SENTINEL, name: "+ Create new project" },
      ]);
      setNewName("");
      setNewDesc("");
      setCreateOpen(false);
      setAssociatedProjectId(created._id);
    } finally {
      setCreating(false);
    }
  };

  const createNewProject = async (name, description) => {
    try {
      const formData = new FormData();
      formData.append("project_name", name);
      formData.append("project_description", description);
      const data = await createProjectAPI(formData);
      return data;
    } catch (error) {}
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const newFile = files[0];
      const data = await uploadProjectFileValidation(projectId, newFile);
      setFileList([...fileList, { id: data.file_id, name: newFile.name }]);
    }
    e.target.value = "";
  };

  const handleRemove = async (id) => {
    await deleteUploadedFileValidation(projectId, id); // Remove from backend
    setFileList((prev) => prev.filter((file) => file.id !== id)); // Remove from UI
  };

  return (
    <Box pt={2}>
      <Typography variant="h5">Project Details</Typography>
      <Typography variant="body1" color="textSecondary">
        View and update project information.
      </Typography>
      <Divider style={{ marginTop: 10, marginBottom: 24 }} />

      <Grid container spacing={3}>
        {/* Row 1: Name + Associated Project (same line) */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Name"
            variant="outlined"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            InputProps={{ style: whiteInput }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {/* Outlined white select using TextField */}
          <TextField
            select
            label="Associated Project"
            variant="outlined"
            fullWidth
            value={associatedProjectId}
            onChange={(e) => handleAssociatedProjectChange(e)}
            InputProps={{ style: whiteInput }}
          >
            {associatedProjects.map((p) => (
              <MenuItem key={p._id} value={p._id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Row 2: Description */}
        <Grid item xs={12}>
          <TextField
            label="Description"
            variant="outlined"
            fullWidth
            multiline
            rows={6}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            InputProps={{ style: whiteInput }}
          />
        </Grid>
        {/* File attachment below description */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" style={{ marginRight: 8 }}>
              Attach files:
            </Typography>
            <input
              id={inputId}
              type="file"
              style={{ display: "none" }}
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
            />
            <label htmlFor={inputId}>
              <IconButton
                component="span"
                size="small"
                aria-label="Attach file"
              >
                <AttachFileIcon />
                <span
                  style={{ marginLeft: 4, fontWeight: "bold", fontSize: 18 }}
                >
                  +
                </span>
              </IconButton>
            </label>
          </Box>
          {/* Attached files as chips */}
          {fileList.length > 0 && (
            <Box mt={1} display="flex" flexWrap="wrap">
              {fileList.map((file) => {
                return (
                  <Box mr={1} mb={1}>
                    <Chip
                      label={file.name} // <-- Only show file name
                      onDelete={() => handleRemove(file.id)}
                      deleteIcon={<CloseIcon fontSize="small" />}
                      variant="default"
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Actions */}
      <Box
        display="flex"
        justifyContent="flex-end"
        width="100%"
        style={{ gap: 12, flexWrap: "wrap", paddingTop: 20 }}
      >
        {fileList.length === 0 ? (
          <>
            <Tooltip title="You need to attach a file to update" arrow>
              <Button
                disabled={true}
                onClick={onUpdate}
                color="quCreateColor"
                startIcon={<SaveIcon />}
              >
                Update
              </Button>
            </Tooltip>
            <Tooltip
              title="You need to attach a file to move to next step"
              arrow
            >
              <Button
                disabled={true}
                onClick={onNext}
                color="quCreateColor"
                endIcon={<NavigateNextIcon />}
              >
                Next
              </Button>
            </Tooltip>
          </>
        ) : (
          <>
            <Button
              onClick={onUpdate}
              color="quCreateColor"
              startIcon={<SaveIcon />}
            >
              Update
            </Button>
            <Button
              onClick={onNext}
              color="quCreateColor"
              endIcon={<NavigateNextIcon />}
            >
              Next
            </Button>
          </>
        )}
      </Box>
      <Dialog
        open={createOpen}
        onClose={!creating ? handleClose : undefined}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create a new project</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Project name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
            margin="dense"
            required
          />
          <TextField
            fullWidth
            label="Project Description"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            margin="dense"
            multiline
            rows={3}
          />
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ marginTop: 116 }}
          >
            A new project will be created and selected as the Associated
            Project.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            color="quSandBoxColor"
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
          >
            {creating ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
